import { Flex, IconButton, Text } from '@hubertai/hubert-component-library';
import { Link } from '@radix-ui/themes';
import { UserRoundSearch, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { HubertTooltip } from '../HubertTooltip';
import type { AnswerListFilter, AnswerOptionSelection } from './useAnswerFilter';
import { summarize, trackAnswerFilter, useAnswerFilterQuestions, withTicks } from './useAnswerFilter';

/** Active filters listed under the toolbar before "+N more". */
const MAX_SHOWN = 3;

/**
 * The toolbar button that opens and closes filter mode, and the active filters under the toolbar: a small outlined
 * box with the count, Clear all and at most MAX_SHOWN lines ("Question: answers" with ×), so it never grows over the
 * candidate list.
 */
const AnswerFilterButton = ({
  jobId,
  listFilter,
  selected,
  onChange,
  open,
  onToggle,
}: {
  jobId: string;
  listFilter: AnswerListFilter;
  selected: AnswerOptionSelection[];
  onChange: (selected: AnswerOptionSelection[]) => void;
  open: boolean;
  onToggle: () => void;
}) => {
  const { t } = useTranslation();
  // The same query as the panel, served from Apollo's cache: no extra request.
  const { questions } = useAnswerFilterQuestions(jobId, open || selected.length > 0, listFilter);
  const active = selected.flatMap(({ questionId, optionIds }) => {
    const question = questions.find((x) => x.id === questionId);
    return question ? [{ question, optionIds }] : [];
  });
  const clearAll = (e: React.MouseEvent) => {
    e.preventDefault();
    trackAnswerFilter([]);
    onChange([]);
  };

  return (
    <>
      <HubertTooltip tooltip={t('swiper.page.answerFilter.button')}>
        <IconButton
          color={open || selected.length > 0 ? 'violet' : 'gray'}
          variant="soft"
          highContrast={open || selected.length > 0}
          size="2"
          className="border border-gray-200"
          aria-label={t('swiper.page.answerFilter.button')}
          aria-pressed={open}
          data-ph-capture-attribute-region="answer_filter"
          onClick={onToggle}
        >
          <UserRoundSearch size={20} />
        </IconButton>
      </HubertTooltip>

      {/* Active filters under the toolbar, in a thin rounded white outline: a compact heading (count, "+N more" when over three, Clear
          all) and at most three lines, so it stays small and never grows over the candidate list. */}
      {active.length > 0 && (
        <Flex
          direction="column"
          className="mb-2 w-0 min-w-full basis-full rounded-[var(--radius-3)] border border-[var(--gray-a5)] bg-white px-2.5 py-1.5"
        >
          <Flex align="center" gap="2" height="20px">
            <Link
              size="1"
              color="violet"
              weight="medium"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (!open) onToggle();
              }}
            >
              {t('swiper.page.answerFilter.activeFilters', { count: active.length })}
              {active.length > MAX_SHOWN &&
                ` · ${t('swiper.page.answerFilter.moreOptions', { count: active.length - MAX_SHOWN })}`}
            </Link>
            <Link size="1" color="gray" href="#" onClick={clearAll} className="ml-auto">
              {t('swiper.page.answerFilter.clearAll')}
            </Link>
          </Flex>
          {active.slice(0, MAX_SHOWN).map(({ question, optionIds }) => (
            <Flex key={question.id} align="center" gap="1" height="20px">
              <Text size="1" truncate className="min-w-0 flex-1">
                {question.title}: <Text weight="medium">{summarize(question, optionIds)}</Text>
              </Text>
              <IconButton
                size="1"
                variant="ghost"
                color="gray"
                aria-label={`${t('swiper.page.answerFilter.clear')} ${question.title}`}
                onClick={() => {
                  const next = withTicks(selected, question.id, []);
                  trackAnswerFilter(next);
                  onChange(next);
                }}
              >
                <X size={11} />
              </IconButton>
            </Flex>
          ))}
        </Flex>
      )}
    </>
  );
};

export default AnswerFilterButton;
